import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { AlertTriangle, Info, Scale, FileText, Globe, Shield, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";

export default function Disclaimer() {
  return (
    <Layout>
      <Helmet>
        <title>Disclaimer — Limitations of Our Website Safety Tool | Transparency Badge</title>
        <meta name="description" content="Important legal information about our website safety checker. Understand what our results mean, our liability limitations, and why results are informational only." />
        <link rel="canonical" href="https://transparencybadge.com/disclaimer" />
      </Helmet>

      {/* Hero */}
      <section className="border-b bg-gradient-to-b from-accent/50 to-background py-16 md:py-20">
        <div className="container">
          <SectionHeader
            badge="Legal Information"
            title="Disclaimer"
            description="Important legal information about the Universal Website Transparency Badge service and its limitations."
          />
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-20">
        <div className="container max-w-3xl">
          {/* Warning Box */}
          <div className="bg-caution-bg border border-caution/30 rounded-2xl p-8 mb-10">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-caution text-caution-foreground">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground mb-2">Important Notice</h2>
                <p className="text-muted-foreground">
                  Please read this disclaimer carefully before using the Universal Website Transparency Badge service. 
                  By accessing or using this service, you acknowledge that you have read, understood, and agree to be bound by the terms outlined below. If you do not agree with any part of this disclaimer, please discontinue use of the service immediately.
                </p>
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-10">
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                <Scale className="h-5 w-5 text-primary" />
                No Guarantee of Accuracy
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-4">
                <p className="text-muted-foreground">
                  The Universal Website Transparency Badge provides informational assessments based on publicly available data sources. 
                  While we strive for accuracy and reliability, we make no warranties, representations, or guarantees — whether express, implied, statutory, or otherwise — regarding:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {[
                    "The completeness, reliability, accuracy, or timeliness of the data sources we query",
                    "The accuracy of any trust assessment, safety evaluation, or badge color assignment",
                    "The safety, legitimacy, or trustworthiness of any website, regardless of its badge status",
                    "The availability, functionality, or uptime of our service at any given time",
                    "The correctness of SSL certificate status, domain age, or server location information",
                    "The completeness of public threat intelligence feed data or blacklist information",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-muted-foreground">
                  Website safety status can change rapidly as public data sources update. A website that appears safe at one moment may become compromised shortly after, and vice versa. Our assessments reflect data available at the time of analysis and should not be considered permanent or definitive.
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                <Info className="h-5 w-5 text-primary" />
                Informational Purpose Only
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-4">
                <p className="text-muted-foreground">
                  All badge assessments, transparency reports, and safety evaluations provided by this service are strictly for informational and educational purposes only. They should not be considered as, or used as a substitute for:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {[
                    "Legal advice, legal opinion, or legal determination of fraud, scam, or criminal activity",
                    "Financial advice, investment guidance, or recommendation to transact with any website",
                    "Security certification, security audit, or professional endorsement of any website's safety",
                    "A substitute for your own due diligence, research, judgment, and common sense",
                    "Professional cybersecurity assessment or penetration testing results",
                    "Government or regulatory approval or certification of any website",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-muted-foreground">
                  This check is based on public technical safety signals available at the time of analysis. It does not verify ownership, official status, or business practices. Always do your own research before making decisions about any website.
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                <FileText className="h-5 w-5 text-primary" />
                Limitation of Liability
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-4">
                <p className="text-muted-foreground">
                  To the fullest extent permitted by applicable law:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {[
                    "We shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising from your use of or reliance on this service, including but not limited to loss of profits, data, goodwill, or other intangible losses",
                    "We are not responsible for any losses — including financial losses, identity theft, data breaches, or personal harm — resulting from interactions with websites that have received any badge status through our service",
                    "We do not guarantee uninterrupted, timely, secure, or error-free access to this service and are not liable for any service disruptions, downtime, or temporary unavailability",
                    "We are not responsible for any decisions you make based on our assessments, whether those decisions result in positive or negative outcomes",
                    "We are not liable for the actions, policies, practices, or content of any third-party websites evaluated through our service",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                <Globe className="h-5 w-5 text-primary" />
                Third-Party Data Sources
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-4">
                <p className="text-muted-foreground">
                  This service relies on data from publicly available threat intelligence feeds, 
                  domain registration sources, SSL certificate authorities, and security databases operated by third parties. We are not responsible for:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {[
                    "The accuracy, completeness, or reliability of data provided by third-party sources",
                    "The availability or uptime of third-party services we query",
                    "Changes to third-party data sources that may affect our assessment results",
                    "The privacy policies or data handling practices of third-party services",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-muted-foreground">
                  Each third-party service is governed by its own terms of service and privacy policy. We encourage users to review those policies independently.
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                <Users className="h-5 w-5 text-primary" />
                User Responsibility
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-4">
                <p className="text-muted-foreground">
                  You are solely responsible for your own online safety decisions. The badge assessments 
                  are one tool among many that should inform your judgment, but they should never replace 
                  careful consideration, independent verification, and common sense when interacting with websites online.
                </p>
                <p className="text-muted-foreground">
                  We strongly recommend that you:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {[
                    "Always verify important information through multiple independent sources",
                    "Exercise caution when sharing personal or financial information online",
                    "Use additional security tools and practices to protect yourself",
                    "Stay informed about current online threats and scam tactics",
                    "Report suspicious websites to appropriate authorities",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                <Shield className="h-5 w-5 text-primary" />
                Intellectual Property
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-4">
                <p className="text-muted-foreground">
                  All content, design, graphics, logos, icons, and other materials on this website are the property of the Universal Website Transparency Badge project and are protected by applicable intellectual property laws. You may not reproduce, distribute, modify, or create derivative works from our content without prior written permission.
                </p>
                <p className="text-muted-foreground">
                  The transparency reports generated by our service are provided for personal, non-commercial use. You may share transparency report results for informational purposes, provided you include appropriate attribution to the Universal Website Transparency Badge.
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold text-foreground mb-4">Indemnification</h2>
              <div className="bg-card rounded-xl border p-6">
                <p className="text-muted-foreground">
                  You agree to indemnify, defend, and hold harmless the Universal Website Transparency Badge, its operators, contributors, and affiliates from and against any claims, liabilities, damages, losses, costs, or expenses (including reasonable legal fees) arising from or related to your use of the service, your violation of this disclaimer, or your violation of any rights of a third party.
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold text-foreground mb-4">Governing Law</h2>
              <div className="bg-card rounded-xl border p-6">
                <p className="text-muted-foreground">
                  This disclaimer shall be governed by and construed in accordance with applicable laws, without regard to conflict of law principles. Any disputes arising from or related to this disclaimer or the use of our service shall be resolved through appropriate legal channels.
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold text-foreground mb-4">Changes to This Disclaimer</h2>
              <div className="bg-card rounded-xl border p-6">
                <p className="text-muted-foreground">
                  We reserve the right to modify, update, or replace this disclaimer at any time without prior notice. Continued use of the service 
                  after any modifications constitutes your acceptance of the updated terms. We encourage you to 
                  review this page periodically for any changes. The "Last updated" date at the bottom of this page indicates when the most recent revisions were made.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <p className="text-sm text-muted-foreground mb-4">
              For more information about how we operate, please visit:
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild variant="outline">
                <Link to="/privacy">Privacy Policy</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/terms">Terms of Service</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/transparency">Transparency & Limitations</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Last Updated */}
      <section className="py-8 border-t">
        <div className="container max-w-3xl text-center">
          <p className="text-sm text-muted-foreground">
            Last updated: March 2026
          </p>
        </div>
      </section>
    </Layout>
  );
}